import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import Button from '@repo/ui/button'
import UploadFileInput from '@repo/ui/file-input'
import { UploadIcon } from '@repo/ui/icons'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z, ZodType } from 'zod'

import TextInput from '../../../../../../packages/ui/components/atoms/input/text-input'
import { FileInput } from '../../../lib/types/models/file/file-input'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'

type HostFamilyUploadFileInput = {
  file: FileInput
}

type Props = {
  className?: string
  defaultValues?: HostFamilyUploadFileInput
  onConfirm: (values: HostFamilyUploadFileInput) => void
  onCancel: () => void
}

const HOST_FAMILY_UPLOAD_FILE_SCHEMA: ZodType<HostFamilyUploadFileInput> = z.object({
  file: FILE_VALIDATION()
})

const HostFamilyUploadFileForm = ({ defaultValues, className, onConfirm, onCancel }: Props) => {
  const form = useForm<HostFamilyUploadFileInput>({
    resolver: zodResolver(HOST_FAMILY_UPLOAD_FILE_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onConfirm)}>
        <div className={'divide-y divide-white-active'}>
          <div className={cn('flex flex-col gap-3', className)}>
            <div className="h-3" />
            <TextInput
              type={'textarea'}
              label="Caption"
              value={form.watch('file.caption')}
              onChange={(e) => {
                form.setValue('file.caption', e.target.value)
              }}
            />
            <FormField
              control={form.control}
              name={'file'}
              render={({ field, fieldState }) => {
                return (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <UploadFileInput
                        placeholder="Drag and drop or browse"
                        types={[FileTypeEnum.IMAGE, FileTypeEnum.VIDEO]}
                        icon={UploadIcon}
                        hasError={!!fieldState.error}
                        label={'Upload document'}
                        files={field?.value ? [field?.value] : undefined}
                        onFilesChange={(files) => {
                          const currentCaption = form.getValues('file.caption')

                          if (files?.length === 0) {
                            field.onChange(null)
                          } else {
                            // Preserve the caption when updating the file
                            const newFile = files[0]
                            newFile.caption = currentCaption
                            field.onChange(newFile)
                          }
                        }}
                        onFileError={(error) => {
                          if (error) toast.error(error)
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>
          <div className="py-6 px-4">
            <div className="flex justify-end gap-2.5">
              <Button variant="ghost" label="Cancel" onClick={onCancel} />
              <Button
                type="button"
                variant="primary"
                label="Confirm"
                onClick={() => form.handleSubmit(onConfirm)()}
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  )
}

export default HostFamilyUploadFileForm
